import React, { useEffect, useRef } from "react";
import styled from "styled-components";

import { useTranslate } from '@tolgee/react';
import TimePicker from "../Meetings/TimePicker";
import MeetingDateTimePicker from "../Meetings/MeetingDateTimePicker";
import { ReactComponent as RightArrow } from "../../Assets/icons/rightArrow.svg";
import { ReactComponent as CloseBtn } from "../../Assets/iconsv2/generic/closeBtn.svg";

import { cloneDeep } from "lodash";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import ProjectSelector from "../GenericComponents/ProjectSelector";
import { CONFERENCE_ROLE_ORGANIZER, CONFERENCE_ROLE_PRESENTER, CONFERENCE_ROLE_ATTENDEE } from "../../Constants/constantValues";

const NewConferenceDetails = ({
  handleInputChange,
  inputValues,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  organizers,
  presenters,
  setAddParticipants,
  setActiveRole,
  handleCreateConference,
  otherParticipants,
  handleCloseAddNewConference,
  allowMic,
  allowCamera,
  allowChat,
  setAllowChat,
  allowPolls,
  setAllowPolls,
  allowInPersonJoining,
  setAllowInPersonJoining,
  allowAutoApprove,
  setAllowAutoApprove,
  headerImageFile,
  headerImagePreviewUrl,
  setHeaderImageFile,
  localPolls = [],
  setManagePollsOpen = () => {},
  projects = [],
  projectId,
  setProjectId,
  isMultipleProjects,
}) => {
  const headerImageInputRef = useRef(null);

  const invitees = otherParticipants
    .map((participant) => {
      if (typeof participant === "string") return participant.trim();
      return participant?.name || participant?.email || "";
    })
    .filter(Boolean);

  const now = new Date();
  const cloneStart = cloneDeep(startDate);
  const isToday = cloneStart.toDateString() === now.toDateString();
  const minTime = isToday ? now : new Date(cloneStart.setHours(0, 0, 0, 0));

  const calculateMinEndTime = () => {
    if (endDate.toDateString() === cloneStart.toDateString()) {
      return cloneStart;
    }
    return null;
  };

  const minEndDateTime =
    endDate.toDateString() === startDate.toDateString()
      ? new Date(Math.max(startDate.getTime(), now.getTime()))
      : now;

  function auto_grow(element) {
    element.style.height = "5px";
    element.style.height = element.scrollHeight + "px";
  }

  useEffect(() => {
    const detailsEl = document.getElementById("resizeTa-3");
    if (detailsEl) auto_grow(detailsEl);

    const termsEl = document.getElementById("resizeTa-terms-3");
    if (termsEl) auto_grow(termsEl);
  }, [inputValues]);
  const { t } = useTranslate();
  const actionText = t("conferences.create_conference");

  const handleHeaderImageChange = (e) => {
    const file = e.currentTarget.files?.[0] || null;
    setHeaderImageFile(file);
  };

  const clearHeaderImage = () => {
    setHeaderImageFile(null);
    if (headerImageInputRef.current) headerImageInputRef.current.value = "";
  };

  return (
    <Container>
      <Header>
        <TitleWrapper>
          <Title>{actionText}</Title>
        </TitleWrapper>
        <HeaderActions>
          {isMultipleProjects && (
            <ProjectSelector projects={projects} value={projectId} onChange={setProjectId} />
          )}
          <CloseButton onClick={() => handleCloseAddNewConference()}>
            <CloseBtn />
          </CloseButton>
        </HeaderActions>
      </Header>
      <CardInputsWrapper>
        <Group>
          <OptionWrapper>
            <CustomNameInput
              value={inputValues.conferenceTitle}
              placeholder={t('new_chat.title')}
              onChange={(e) => handleInputChange(e)}
              name="conferenceTitle"
              maxLength={100}
            />
            <Separator />
            <CustomTextArea
              id="resizeTa-3"
              value={inputValues.conferenceDetails}
              placeholder={t('general.details')}
              onChange={(e) => handleInputChange(e)}
              name="conferenceDetails"
              maxLength={1000}
            />
            <Separator />
            <CustomTextArea
              id="resizeTa-terms-3"
              value={inputValues.conferenceTermsAndConditions}
              placeholder={t("general.terms_and_conditions")}
              onChange={(e) => handleInputChange(e)}
              name="conferenceTermsAndConditions"
              maxLength={5000}
            />
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper>
            <FileRow>
              <FileLabel>{t("chat.image")}</FileLabel>
              <FileActions>
                <HiddenFileInput
                  ref={headerImageInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleHeaderImageChange}
                />
                <FileActionButton
                  type="button"
                  onClick={() => headerImageInputRef.current?.click()}
                >
                  {headerImageFile ? t("settings.change") : t("general.add")}
                </FileActionButton>
                {headerImageFile && (
                  <FileActionButton type="button" onClick={clearHeaderImage}>
                    {t("general.remove")}
                  </FileActionButton>
                )}
              </FileActions>
            </FileRow>
            {headerImageFile?.name && (
              <>
                <Separator />
                <SelectedFileRow>
                  <SelectedFileName title={headerImageFile.name}>
                    {headerImageFile.name}
                  </SelectedFileName>
                </SelectedFileRow>
              </>
            )}
            {headerImagePreviewUrl && (
              <>
                <Separator />
                <PreviewContainer>
                  <PreviewImage src={headerImagePreviewUrl} alt="Header preview" />
                </PreviewContainer>
              </>
            )}
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper
            onClick={() => {
              setActiveRole(CONFERENCE_ROLE_ORGANIZER);
              setAddParticipants(true);
            }}
            style={{ cursor: "pointer" }}
          >
            <InvitesButton type="button">
              {t('conferences.organizer')}
              <p>{organizers.length}</p>
              <RightArrow />
            </InvitesButton>
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper
            onClick={() => {
              setActiveRole(CONFERENCE_ROLE_PRESENTER);
              setAddParticipants(true);
            }}
            style={{ cursor: "pointer" }}
          >
            <InvitesButton type="button">
              {t('conferences.presenters')}
              <p>{presenters.length}</p>
              <RightArrow />
            </InvitesButton>
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper>
            <DateTimeWrapper>
              <DateInfo>{t('new_chat.starts')}</DateInfo>
              <MeetingDateTimePicker
                slim
                format={"dd-MM-yyyy"}
                value={startDate}
                setValue={setStartDate}
                noCalendar
                id={"start_time_date"}
              />
              <TimePicker
                date={startDate}
                setDate={setStartDate}
                id={"start_time"}
                minTime={minTime}
                minDateTime={now}
              />
            </DateTimeWrapper>
            <Separator />
            <DateTimeWrapper>
              <DateInfo>{t('new_chat.ends')}</DateInfo>
              <MeetingDateTimePicker
                slim
                format={"dd-MM-yyyy"}
                value={endDate}
                setValue={setEndDate}
                noCalendar
                id={"end_time_date"}
              />
              <TimePicker
                date={endDate}
                setDate={setEndDate}
                minTime={calculateMinEndTime()}
                minDateTime={minEndDateTime}
                id={"end_time"}
              />
            </DateTimeWrapper>
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper
            onClick={() => {
              setActiveRole(CONFERENCE_ROLE_ATTENDEE);
              setAddParticipants(true);
            }}
            style={{ cursor: "pointer" }}
          >
            <InvitesButton type="button">
              {t('conferences.invitees')}
              <p>{otherParticipants.length}</p>
              <RightArrow />
            </InvitesButton>
            {invitees.length > 0 && (
              <ParticipantsContainer>
                {invitees.map((item, index) => (
                  <InvitedPeople key={index}>
                    <p>{item}</p>
                  </InvitedPeople>
                ))}
              </ParticipantsContainer>
            )}
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper>
            <ToggleRow>
              <ToggleLabel>{t('conferences.allow_mic')}</ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowMic}
                disabled
                aria-pressed={allowMic}
              >
                <SwitchThumb $active={allowMic} />
              </ToggleSwitch>
            </ToggleRow>
            <ToggleRow>
              <ToggleLabel>{t('conferences.allow_camera')}</ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowCamera}
                disabled
                aria-pressed={allowCamera}
              >
                <SwitchThumb $active={allowCamera} />
              </ToggleSwitch>
            </ToggleRow>
            <ToggleRow>
              <ToggleLabel>{t('conferences.allow_chat')}</ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowChat}
                onClick={() => setAllowChat((prev) => !prev)}
                aria-pressed={allowChat}
              >
                <SwitchThumb $active={allowChat} />
              </ToggleSwitch>
            </ToggleRow>
            <ToggleRow>
              <ToggleLabel>{t('conferences.allow_polls')}</ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowPolls}
                onClick={() => setAllowPolls((prev) => !prev)}
                aria-pressed={allowPolls}
              >
                <SwitchThumb $active={allowPolls} />
              </ToggleSwitch>
            </ToggleRow>
            <ToggleRow>
              <ToggleLabel>{t('conferences.allow_in_person_joining')}</ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowInPersonJoining}
                onClick={() => setAllowInPersonJoining((prev) => !prev)}
                aria-pressed={allowInPersonJoining}
              >
                <SwitchThumb $active={allowInPersonJoining} />
              </ToggleSwitch>
            </ToggleRow>
            <ToggleRow>
              <ToggleLabel>{t('conferences.auto_approve_participants')}</ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowAutoApprove}
                onClick={() => setAllowAutoApprove((prev) => !prev)}
                aria-pressed={allowAutoApprove}
              >
                <SwitchThumb $active={allowAutoApprove} />
              </ToggleSwitch>
            </ToggleRow>
          </OptionWrapper>
        </Group>
        {allowPolls && (
          <Group>
            <OptionWrapper
              onClick={() => setManagePollsOpen(true)}
              style={{ cursor: "pointer" }}
            >
              <InvitesButton type="button">
                {t('conferences.polls')}
                <p>{localPolls.length}</p>
                <RightArrow />
              </InvitesButton>
            </OptionWrapper>
          </Group>
        )}
        <Group>
            <PrimaryButton
              style={{ padding: "16px", width: "100%" }}
              disabled={
                inputValues.conferenceTitle.length <= 2 ||
                presenters?.length === 0 ||
                (organizers?.length === 0 &&
                  presenters?.length === 0 &&
                  otherParticipants?.length === 0)
              }
              onClick={() => handleCreateConference()}
          >
            {actionText}
          </PrimaryButton>
        </Group>
      </CardInputsWrapper>
    </Container>
  );
};

export default NewConferenceDetails;
const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  gap: 25px;
  background: ${({ theme }) => theme.chat.conversationBackground};
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Title = styled.p`
 text-transform: capitalize;
   font-weight: 700;
font-size: 20px;
line-height:32px;
letter-spacing: 0.04px;
color:${({ theme }) => theme.menu.primaryTextColor};
`;

const CardInputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 0 auto;
  padding: 0 21px;
  width: 100%;
  max-width: 631px;
  overflow: auto;
`;

const Group = styled.div`
  display: flex;
  border-radius: 4px;
  align-items: center;
  width: 100%;
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 4px;
  width: 100%;
`;

const CustomInput = styled.input`
  background: none;
  border: none;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.40799999237060547px;
  width: 100%;
  padding: 15px 16px;
  &:focus-visible {
    outline: none;
  }
  &::placeholder {
    font-weight: 400;
  }
`;

const CustomNameInput = styled(CustomInput)`
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const DateTimeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
`;

const DateInfo = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const InvitesButton = styled.button`
  display: flex;
  align-items: center;
  border-radius: 5px;
  border: none;
  padding: 14px 16px;
  cursor: pointer;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  font-size: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-weight: 500;
  gap: 10px;
  width: 100%;
  text-align: left;
  p {
    margin-left: auto;
  }
  svg path {
    stroke: ${({ theme }) => theme.menu.secondaryTextColor};
  }
`;

const ParticipantsContainer = styled.div`
  padding-bottom: 8px;
`;

const InvitedPeople = styled.div`
  overflow: hidden;
  display: flex;
  width: 100%;
  padding: 4px 16px;
  p {
    font-size: 14px;
    line-height: 18px;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: left;
  }
`;

const Separator = styled.div`
  width: 100%;
  min-height: 1px;
  background: ${({ theme }) => theme.sidebar.borderColor};
`;

const CustomTextArea = styled.textarea`
  padding: 15px 16px;
  width: 100%;
  resize: none;
  background: none;
  min-height: 80px;
  border: none;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  max-height: 250px;
  &:focus-visible {
    outline: none;
  }
  &::placeholder {
    font-weight: 400;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 16px;
`;

const ToggleLabel = styled.span`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 16px;
  font-weight: 500;
`;

const ToggleSwitch = styled.button`
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? (theme.premium_green || '#11B973') : theme.sidebar.borderColor};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  background: ${({ $active, theme }) =>
    $active ? (theme.premium_green || '#11B973') : theme.menu.groupContentBackground};
  transition: background 0.2s ease-in-out, border 0.2s ease-in-out;
  padding: 0;
`;

const SwitchThumb = styled.span`
  position: absolute;
  top: 50%;
  left: ${({ $active }) => ($active ? '22px' : '2px')};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ $active, theme }) =>
    $active ? '#FFFFFF' : (theme.menu.secondaryTextColor || '#777')};
  transform: translateY(-50%);
  transition: left 0.2s ease-in-out, background 0.2s ease-in-out, transform 0.2s ease-in-out;
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    height: 26px;
    width: 26px;
    color: ${({ theme }) => theme.menu.selectedOpionBackground};
    stroke: ${({ theme }) => theme.sidebar.borderColor};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 16px;
  align-items: center;
  background: ${({ theme }) => theme.chat.conversationBackground};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  gap: 20px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 16px;
`;

const FileLabel = styled.span`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 16px;
  font-weight: 500;
`;

const FileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FileActionButton = styled.button`
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.groupContentBackground};
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const SelectedFileRow = styled.div`
  padding: 10px 16px;
`;

const SelectedFileName = styled.div`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const PreviewContainer = styled.div`
  padding: 12px 16px;
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 180px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;
